/**
 * SEO config + pure meta builder for Conscierra (client-portal).
 *
 * Conscierra is MULTI-REGION (conscierra.au / .sg / .id / .fj). The canonical
 * origin is therefore resolved per-region from the active RegionConfig.host
 * — which useRegion() derives from the build-time VITE_HOST during per-region
 * SSG (so dist/sg/* bakes conscierra.sg) and from window.location at runtime —
 * NOT a hardcoded constant. This module is kept pure and framework-free so it
 * unit-tests without a DOM or router. Consumed by <Seo>. Go-live readiness P0 #6.
 *
 * (Replaces the prior usePageMeta hook, which set meta via useEffect — that runs
 * only after hydration, so the prerendered HTML shipped the Home meta on every
 * page. <Seo> renders through vite-react-ssg's <Head>, captured at prerender.)
 */
export const SITE_NAME = 'Conscierra';

/**
 * Default Open Graph image. Uses the existing brand icon; it is a square icon
 * rather than a 1200x630 social card — replacing it with a proper OG card is a
 * tracked P2 (readiness report §4), out of scope for this meta-correctness fix.
 */
export const DEFAULT_OG_IMAGE = '/brand/conscierra-icon-fern.png';

export interface PageMeta {
  title: string;
  description: string;
  /** Canonical path under the region origin, e.g. "/about". Defaults to "/". */
  canonicalPath?: string;
  /** OG image: absolute URL or root-relative path. Defaults to DEFAULT_OG_IMAGE. */
  ogImage?: string;
  noindex?: boolean;
}

export interface SeoMeta {
  fullTitle: string;
  description: string;
  robots: string;
  canonicalUrl: string;
  imageUrl: string;
}

/**
 * Build resolved meta for a page against a region's canonical host. Pure:
 * same (host, input) -> same output, no window/document reliance, so it is
 * correct during static prerender.
 */
export function buildSeoMeta(
  host: string,
  { title, description, canonicalPath = '/', ogImage, noindex = false }: PageMeta,
): SeoMeta {
  const origin = `https://${host}`;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const path = canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`;
  // Collapse root to the bare origin (no trailing slash) to avoid a "/" vs ""
  // duplicate-URL signal.
  const canonicalUrl = path === '/' ? origin : `${origin}${path}`;
  const imageUrl = ogImage
    ? ogImage.startsWith('http')
      ? ogImage
      : `${origin}${ogImage}`
    : `${origin}${DEFAULT_OG_IMAGE}`;
  return {
    fullTitle,
    description,
    robots: noindex ? 'noindex, nofollow' : 'index, follow',
    canonicalUrl,
    imageUrl,
  };
}
